/**
 * @fileoverview The card's tiles for a plant or a bed (B136 + B138, B158 job 3; design.md rev 12): two rows of the app's
 * rounded-square tile (TileButton) and the trays the second row opens. Row 1 is Share, filled, two-thirds wide with the count of
 * how often its links were opened, and Public, one-third, the switch (PublicPageSwitch's tile look, for a plant or a bed). Row 2 is
 * To-dos (its count; terracotta when one is due today), Notes (its count) and, when the card gives one, a third tile (the plant
 * card gives Doctor); without it the row is two columns.
 *
 * The states, drawn as the server and the plan say them: with no photo yet Share and Public are dim (aria-disabled, a tap says
 * to add a photo first) while To-dos and Notes stay live; in someone else's garden there is Share alone, full width; an archived
 * card has no tiles but Notes, its journal read-only. The To-dos tray is the subject's own to-dos (OwnTodosRow in tray mode) and
 * the Notes tray its journal (NotesSection in tray mode), each ending in the shared TrayHide. `notesLead` is drawn first inside
 * the open Notes tray, above the notes. Which tray is open is the card's one-tray-at-a-time state, so the card holds it and
 * gives it here. The tiles decide nothing: the due-today flag, the counts and the sharing are the server's.
 *
 * Where: the plant card (PlantCard), under the Care strip; the bed card (LocationCard, B158 job 7), under This week, which gives a bed
 * subject, no third tile, its own share words and the note-for-every-plant door as `notesLead`.
 * API: none of its own; the due-today flag is the plants brief's (todos.dueToday), the count of to-dos is the plants brief's
 * todos.open, the Share count the plants brief's sharing.
 * Words (en/es): plants.card.tiles.* (share, public, todos, notes, hide), plants.share.noPhotosYet, plants.card.public.needsPhoto,
 * plants.share.title, plants.card.actions.share (a bed's Share label).
 *
 * @module CardTiles
 */

import React from 'react';
import { CircleCheck, NotebookPen, Send } from 'lucide-react';
import { OwnTodosRow } from '@/features/own-todos/OwnTodosRow';
import { PublicPageSwitch } from '@/features/share/PublicPageSwitch';
import { TileButton } from '@/shared/components/ui/TileButton';
import { TrayHide } from '@/shared/components/ui/TrayHide';
import type { Subject } from '@/shared/api/subject';
import { toast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { opensWords } from '@/shared/hooks/useShareCount';
import type { PlantSharing } from '@/shared/api/shares';
import { journalNotes, type Note } from '@/shared/api/notes';
import { NotesSection } from '../NotesSection';
import { useBringIntoView } from '@/shared/hooks/useBringIntoView';
import { cn } from '@/lib/utils';

/** The card's trays: the planting windows, Care, the weather's story, the to-dos and the notes; only one is open at a time. */
export type CardTray = 'planting' | 'care' | 'week' | 'todos' | 'notes' | null;

/** What the tiles are given. */
interface CardTilesProps {
  /** What the card is about: a plant (plantSubject) or a bed (locationSubject). */
  subject: Subject;
  /** Its name for the to-do sheet's title. */
  name: string;
  /** Whether it has a photo: Share and Public are dim without one. */
  hasPhotos: boolean;
  /** An archived plant: no tiles but Notes, read-only. */
  archived: boolean;
  /** The gardener may change the plant (their own garden, Edit offered): Public is shown. Without it Share stands alone. */
  showPublic: boolean;
  /** How many times the plant's share links were opened; 0 for none. */
  opens: number;
  /** The plant's sharing from the plants brief; null until the brief is read; undefined when it could not be read. */
  sharing?: PlantSharing | null;
  /** An own to-do about it is due today (the plants brief's todos.dueToday, the server's yes or no): the To-dos tile is tinted. */
  dueToday: boolean;
  /** How many open own to-dos it has (the plants brief's todos.open): the To-dos tile's count. */
  todoCount: number;
  /** The plant's notes as the garden's list has them (the Notes tile counts the journal's). */
  notes: Note[] | undefined;
  /** The plant is public (a note saved from a checkup can then be hidden from the public pages). */
  plantPublic: boolean;
  /** Which tray is open on the card. */
  tray: CardTray;
  /** Opens a tray, or shuts the open one (null). */
  onTray: (next: CardTray) => void;
  /** Opens the share studio. */
  onShare: () => void;
  /** The row's third tile (the plant card's Doctor); without it the row is two columns. */
  third?: React.ReactNode;
  /** A row drawn first inside the open Notes tray, above the notes (the bed card's note-for-every-plant door). */
  notesLead?: React.ReactNode;
  /** The card's own words for "nothing to share yet" (a bed's); the plant's when absent. Used for the toast, Share's title and Public's reason. */
  noPhotosWords?: string;
  /** The name Share's label carries ("Share this bed: Bed 5"); the plant's own label when absent. */
  shareName?: string;
  /** Called after a note was saved or removed in the Notes tray (the bed card reads its list again). */
  onNotesChanged?: () => void;
}

/**
 * The two rows of tiles and the open tray.
 *
 * @param props - CardTilesProps.
 */
export const CardTiles: React.FC<CardTilesProps> = ({
  subject, name, hasPhotos, archived, showPublic, opens, sharing, dueToday, todoCount, notes, plantPublic, tray, onTray, onShare, third, notesLead, noPhotosWords, shareName, onNotesChanged,
}) => {
  const { reactTranslate: t, i18n } = useTranslation();
  /** The open To-dos and Notes trays, each brought into view when it opens inside the plant pop-up (B153). */
  const todosTrayRef = useBringIntoView(tray === 'todos', true);
  const notesTrayRef = useBringIntoView(tray === 'notes', true);
  /** The count short however large it grows (8, 950, 1.2K), and "opened 3 times". */
  const { short: opensText, phrase: visitsPhrase } = opensWords(opens, i18n.language, t);
  /** The journal's own notes: the ones the Notes tray lists. */
  const noteCount = journalNotes(notes).length;
  /** Why there is nothing to share yet: the card's words, or the plant's. */
  const noPhotos = noPhotosWords ?? t('plants.share.noPhotosYet');
  /** Share's label for a screen reader: the plant's, or the card's name with it. */
  const shareLabel = shareName ? `${t('plants.card.actions.share')}: ${shareName}` : t('plants.share.title');

  /**
   * Opens a tray, or shuts it when it is the open one.
   *
   * @param next - The tray the tile opens.
   */
  const toggle = (next: Exclude<CardTray, null>): void => onTray(tray === next ? null : next);

  /** "Hide": shuts the open tray. */
  const hide = <TrayHide onClick={() => onTray(null)}>{t('plants.card.tiles.hide')}</TrayHide>;

  /** The Notes tile (an archived card's only one). */
  const notesTile = (
    <TileButton size="small" icon={<NotebookPen />} badge={noteCount} open={tray === 'notes'} aria-expanded={tray === 'notes'} onClick={() => toggle('notes')}>
      {t('plants.card.tiles.notes')}
    </TileButton>
  );

  return (
    <>
      <div className="flex flex-col gap-2 px-4 pb-3 pt-2.5">
        {!archived && (
          <div className={cn('grid gap-2', showPublic ? 'grid-cols-[2fr_1fr]' : 'grid-cols-1')}>
            {/* Without a photo there is nothing to share yet: dim, and a tap says what comes first rather than opening a sheet only to refuse. */}
            <TileButton
              size="big"
              filled
              icon={<Send />}
              badge={opensText}
              dim={!hasPhotos}
              onClick={() => (hasPhotos ? onShare() : toast({ description: noPhotos }))}
              aria-label={opens > 0 ? `${shareLabel} · ${visitsPhrase}` : shareLabel}
              title={hasPhotos ? (visitsPhrase ?? shareLabel) : noPhotos}
            >
              {t('plants.card.tiles.share')}
            </TileButton>
            {showPublic && (
              <PublicPageSwitch subject={subject} briefSharing={sharing} look="tile" disabledReason={hasPhotos ? undefined : (noPhotosWords ?? t('plants.card.public.needsPhoto'))} />
            )}
          </div>
        )}
        {archived ? notesTile : (
          <div className={cn('grid gap-2', third ? 'grid-cols-3' : 'grid-cols-2')}>
            <TileButton size="small" icon={<CircleCheck />} badge={todoCount} due={dueToday} open={tray === 'todos'} aria-expanded={tray === 'todos'} onClick={() => toggle('todos')}>
              {t('plants.card.tiles.todos')}
            </TileButton>
            {notesTile}
            {third}
          </div>
        )}
      </div>

      {!archived && tray === 'todos' && (
        <div ref={todosTrayRef} className="border-t bg-background pb-1 pt-2">
          <OwnTodosRow about={subject.kind === 'plant' ? 'plant' : 'bed'} id={subject.id} name={name} />
          {hide}
        </div>
      )}
      {tray === 'notes' && (
        <div ref={notesTrayRef} className="border-t bg-background pb-1 pt-2.5">
          {notesLead}
          <NotesSection subject={subject} initialNotes={notes} readOnly={archived} plantPublic={plantPublic} onChanged={onNotesChanged} tray />
          {hide}
        </div>
      )}
    </>
  );
};
